import React, { useState, useEffect } from 'react';
import { 
  Users, Building, CalendarCheck, DollarSign, TrendingUp, 
  AlertTriangle, Clock, CheckCircle, Loader2, Crown, Star, Home,
  RefreshCw, Database, AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAdmin } from '../../context/AdminContext';
import { toast } from 'sonner';

const StatCard = ({ title, value, subValue, icon: Icon, color = 'blue', loading = false }) => {
  const colors = {
    blue: 'bg-blue-500/20 text-blue-400',
    green: 'bg-green-500/20 text-green-400',
    amber: 'bg-amber-500/20 text-amber-400',
    coral: 'bg-coral/20 text-coral'
  };

  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        {subValue && (
          <span className="text-xs text-slate-400">{subValue}</span>
        )}
      </div>
      {loading ? (
        <div className="animate-pulse">
          <div className="h-8 bg-slate-700 rounded w-16 mb-1"></div>
          <div className="h-4 bg-slate-700 rounded w-24"></div>
        </div>
      ) : (
        <>
          <p className="font-heading text-2xl font-bold text-white">{value}</p>
          <p className="text-sm text-slate-400">{title}</p>
        </>
      )}
    </div>
  );
};

const AdminDashboardPage = () => {
  const { getDashboard, getSubscriptionStats } = useAdmin();
  const [stats, setStats] = useState(null);
  const [subscriptionStats, setSubscriptionStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashData, subData] = await Promise.all([
        getDashboard(),
        getSubscriptionStats()
      ]);
      setStats(dashData);
      setSubscriptionStats(subData);
      setLastRefresh(new Date().toLocaleTimeString());
    } catch (err) {
      setError('Failed to load dashboard metrics');
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Error state
  if (error && !stats) {
    return (
      <div className="p-8">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Failed to Load Dashboard</h2>
          <p className="text-slate-400 mb-4">{error}</p>
          <Button onClick={fetchDashboard} className="bg-trust-blue hover:bg-trust-blue-hover">
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white mb-2">
            Dashboard
          </h1>
          <p className="text-slate-400">
            Platform overview and key metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          {stats?.live_data && (
            <Badge className="bg-green-500/20 text-green-400 border border-green-500/30">
              <Database className="w-3 h-3 mr-1" />
              Live Data
            </Badge>
          )}
          {lastRefresh && (
            <span className="text-xs text-slate-500">Updated: {lastRefresh}</span>
          )}
          <Button 
            variant="outline" 
            size="sm"
            className="border-slate-600 text-slate-300"
            onClick={fetchDashboard}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          title="Total Users"
          value={stats?.users?.total || 0}
          subValue={`${stats?.users?.owners || 0} owners`}
          icon={Users}
          color="blue"
          loading={loading}
        />
        <StatCard
          title="Active Listings"
          value={stats?.properties?.active || 0}
          subValue={stats?.properties?.demo > 0 ? `+${stats.properties.demo} demo` : `${stats?.properties?.total || 0} total`}
          icon={Building}
          color="green"
          loading={loading}
        />
        <StatCard
          title="Total Bookings"
          value={stats?.bookings?.total || 0}
          subValue={`${stats?.bookings?.pending || 0} pending`}
          icon={CalendarCheck}
          color="amber"
          loading={loading}
        />
        <StatCard
          title="Payment Volume"
          value={stats?.payments?.total_volume > 0 ? `$${(stats.payments.total_volume).toLocaleString()}` : '$0'}
          subValue={stats?.payments?.successful > 0 ? `${stats.payments.successful} successful` : 'No payments yet'}
          icon={DollarSign}
          color="coral"
          loading={loading}
        />
      </div>

      {/* Subscription Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
          <h2 className="font-heading text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Crown className="w-5 h-5 text-amber-400" />
            Subscription Overview
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Active Subscriptions</span>
              <span className="text-2xl font-bold text-green-400">
                {subscriptionStats?.total_active || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Canceled</span>
              <span className="text-lg font-medium text-slate-500">
                {subscriptionStats?.total_canceled || 0}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 lg:col-span-2">
          <h2 className="font-heading text-xl font-semibold text-white mb-4">
            Tier Distribution
          </h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-slate-700/50 rounded-xl">
              <Home className="w-6 h-6 text-slate-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">
                {subscriptionStats?.by_tier?.standard || 0}
              </p>
              <p className="text-sm text-slate-400">Standard</p>
            </div>
            <div className="text-center p-4 bg-amber-500/10 rounded-xl border border-amber-500/30">
              <Star className="w-6 h-6 text-amber-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-amber-400">
                {subscriptionStats?.by_tier?.featured || 0}
              </p>
              <p className="text-sm text-slate-400">Featured</p>
            </div>
            <div className="text-center p-4 bg-blue-500/10 rounded-xl border border-blue-500/30">
              <Crown className="w-6 h-6 text-trust-blue mx-auto mb-2" />
              <p className="text-2xl font-bold text-trust-blue">
                {subscriptionStats?.by_tier?.concierge || 0}
              </p>
              <p className="text-sm text-slate-400">Concierge</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Platform Health */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
          <h2 className="font-heading text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-trust-blue" />
            Platform Health
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-slate-400">API Status</span>
              <span className="flex items-center gap-2 text-green-400">
                <CheckCircle className="w-4 h-4" />
                Operational
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Database</span>
              <span className="flex items-center gap-2 text-green-400">
                <CheckCircle className="w-4 h-4" />
                Connected
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Webhook Errors (24h)</span>
              <span className={`flex items-center gap-2 ${
                (stats?.webhooks?.recent_errors || 0) > 0 
                  ? 'text-amber-400' 
                  : 'text-green-400'
              }`}>
                {(stats?.webhooks?.recent_errors || 0) > 0 ? (
                  <AlertTriangle className="w-4 h-4" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                {stats?.webhooks?.recent_errors || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
          <h2 className="font-heading text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-trust-blue" />
            Quick Stats
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Renters</span>
              <span className="text-white font-medium">{stats?.users?.renters || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Property Owners</span>
              <span className="text-white font-medium">{stats?.users?.owners || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Approved Bookings</span>
              <span className="text-white font-medium">{stats?.bookings?.approved || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Total Transactions</span>
              <span className="text-white font-medium">{stats?.payments?.total_transactions || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
